import type { Contact } from '@personical/cc-shared-types';
import { vcardAdr, vcardEscape, vcardOrg } from '@personical/cc-shared-types';

const TYPE_FOR_PHONE: Record<string, string> = {
  mobile: 'CELL',
  work: 'WORK,VOICE',
  home: 'HOME,VOICE',
  other: 'VOICE'
};

const TYPE_FOR_EMAIL: Record<string, string> = {
  work: 'WORK',
  personal: 'HOME',
  other: 'INTERNET'
};

const escape = vcardEscape;

/**
 * Browser-side vCard 4.0 emitter — used for client-side downloads. The
 * server has its own copy in apps/func/src/vcard.ts; the two should stay
 * in sync.
 */
export function contactToVCard(c: Contact): string {
  const lines: string[] = [
    'BEGIN:VCARD',
    'VERSION:4.0',
    `UID:urn:uuid:${c.id}`,
    `FN:${escape(c.displayName)}`
  ];
  if (c.jobTitle) lines.push(`TITLE:${escape(c.jobTitle)}`);
  // ORG's second component is the department (RFC 6350 6.6.4).
  const org = vcardOrg(c.company, c.department);
  if (org) lines.push(org);
  if (c.website) lines.push(`URL:${escape(c.website)}`);
  if (c.notes) lines.push(`NOTE:${escape(c.notes)}`);
  for (const e of c.emails) {
    const params = TYPE_FOR_EMAIL[e.type] ?? 'INTERNET';
    lines.push(`EMAIL;TYPE=${params}:${escape(e.address)}`);
  }
  for (const p of c.phoneNumbers) {
    const params = TYPE_FOR_PHONE[p.type] ?? 'VOICE';
    lines.push(`TEL;TYPE=${params}:${escape(p.e164 ?? p.number)}`);
  }
  const adr = vcardAdr(c.address);
  if (adr) lines.push(adr);
  if (c.photoUrl) lines.push(`PHOTO:${escape(c.photoUrl)}`);
  lines.push(`REV:${c.updatedAt.replace(/[-:]/g, '').replace(/\.\d+/, '')}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}

export function contactsToVCardString(contacts: Contact[]): string {
  return contacts.map(contactToVCard).join('\r\n') + '\r\n';
}
